import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../../common/reducers/initContext";
import { dataHookFactory, dataHookWithCredentialsFactory, DefaultFailureAction, DefaultSuccessAction, readonlyHookWithCredentialsFactory } from "../../common/reducers/utilities";
import { ensureArray } from "../../common/utils/arrayUtilities";
import { ensureInteger } from "../../common/utils/generalUtils";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";

const key = e => ensureInteger(e.id);

const equipmentMgrSlice = createSlice({
    name: 'mgrEquipment',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            state.data = ensureArray(action.payload).reduce((obj, e) => { obj[key(e)] = e; return obj; }, {});
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(e => state.data[key(e)] = e);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(e => delete state.data[key(e)]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => equipmentMgrSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'equipment_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'equipment_one');

const fetchAllEquipment = network => network.get('/api/v2/mgrEquipment', equipmentMgrSlice.actions.set, visualizeFailure('get'));

// #endregion

// #region selectors
const selectEquipmentData = state => state.mgrEquipment.data;
const selectAllEquipment = createSelector([selectEquipmentData], equipment => Object.values(equipment))
const selectSingleEquipment = id => createSelector([selectEquipmentData], equipment => equipment[id]);
const selectEquipment = ids => createSelector([selectEquipmentData], equipment => ensureArray(ids).reduce((result, id) => { if (equipment[id]) { result.push(equipment[id]) } return result }, []));
// #endregion

const listen = {
    equipmentCreated: (equipment, { dispatch, websocket }) => {
        markFetched(websocket, 'equipment', key(equipment))
        dispatch(equipmentMgrSlice.actions.addOrUpdate(equipment))
    },
    equipmentUpdated: (equipment, { dispatch }) => dispatch(equipmentMgrSlice.actions.addOrUpdate(equipment)),
    equipmentRemoved: (equipment, { dispatch }) => dispatch(equipmentMgrSlice.actions.remove(equipment)),
}


const fullRoles = ['administrator', 'equipment manager', 'rostering manager', 'recurring rostering manager'];
const readonlyRoles = ['auditor', 'equipment auditor', 'rostering auditor', 'recurring rostering auditor'];

const checkPermissions = (credentials, equipment, requiredRoles, condition = true) => {
    return (
        credentials.hasRole(requiredRoles, { office: equipment?.office }) &&
        condition
    );
};

export const canViewEquipment = (credentials, equipment) => checkPermissions(credentials, equipment, readonlyRoles.concat(fullRoles));
const options = { condition: credentials => canViewEquipment(credentials) };
const singleOptions = { ...options, defaultValue: null };

const init = { run: fetchAllEquipment, listen, immortal: true }

export const [useMgrAllEquipment] = dataHookWithCredentialsFactory(selectAllEquipment, init, options);
export const [useMgrEquipment, realizeMgrEquipment] = dataHookWithCredentialsFactory(selectEquipment, ids => init,  options);
export const [useMgrSingleEquipment] = dataHookWithCredentialsFactory(selectSingleEquipment, id => init, singleOptions);

export const [useReadMgrSingleEquipment] = readonlyHookWithCredentialsFactory(selectSingleEquipment, id => init, singleOptions);
export const [useReadMgrEquipment] = readonlyHookWithCredentialsFactory(selectEquipment, ids => init, options);

const reducer = { name: equipmentMgrSlice.name, reducer: equipmentMgrSlice.reducer }
export default reducer;